import {useEffect, useMemo, useState} from "react";
import debounce from "lodash/debounce";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {Button, Card, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Tooltip} from "@mui/material";
import {RestartAlt} from "@mui/icons-material";
import {format} from "date-fns";
import {COUNTRIES, EMAIL_STATUSES} from "../../Utils/constants";

const FilterControls = ({
                            countryRegion,
                            setCountryRegion,
                            emailStatus,
                            setEmailStatus,
                            selectedWeekDate,
                            setSelectedWeekDate,
                            selectedDate,
                            setSelectedDate,
                            selectedMonth,
                            setSelectedMonth,
                            selectedYear,
                            setSelectedYear,
                            city,
                            setCity,
                            resetFilters,
                            setRefreshGridData,
                            resetLoading,
                        }) => {
    const [localRegionCity, setLocalRegionCity] = useState(city);

    const debouncedRegionCityChange = useMemo(
        () =>
            debounce((value) => {
                setCity(value);
                setRefreshGridData(true);
            }, 500),
        [setCity, setRefreshGridData]
    );

    useEffect(() => {
        setLocalRegionCity(city);
    }, [city]);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Card
                sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: "background.default",
                    borderRadius: 2,
                    boxShadow: 1,
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    {/* Country and Region Filter */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Tooltip title="Filter by country" placement="top">
                            <FormControl fullWidth sx={{minWidth: 180}}>
                                <InputLabel id="country-region-label">Country</InputLabel>
                                <Select
                                    labelId="country-region-label"
                                    value={countryRegion}
                                    onChange={(e) => {
                                        setCountryRegion(e.target.value);
                                        setRefreshGridData(true);
                                    }}
                                    label="Country"
                                    aria-label="Select country"
                                >
                                    {COUNTRIES.map((country) => (
                                        <MenuItem key={country.value} value={country.value}>
                                            {country.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Tooltip>
                    </Grid>

                    {/* Region/City Filter */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Tooltip title="Filter by region or city" placement="top">
                            <TextField
                                label="Region/City"
                                value={localRegionCity}
                                onChange={(e) => {
                                    setLocalRegionCity(e.target.value); // Update local state immediately
                                    debouncedRegionCityChange(e.target.value); // Debounced update for actual state
                                }}
                                fullWidth
                                sx={{minWidth: 180}}
                                aria-label="Enter region or city"
                                placeholder="e.g., Stockholm, Copenhagen"
                            />
                        </Tooltip>
                    </Grid>

                    {/* Email Status Filter */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Tooltip title="Filter by email status" placement="top">
                            <FormControl fullWidth sx={{minWidth: 180}}>
                                <InputLabel id="email-status-label">Email Status</InputLabel>
                                <Select
                                    labelId="email-status-label"
                                    value={emailStatus}
                                    onChange={(e) => {
                                        setEmailStatus(e.target.value);
                                        setRefreshGridData(true);
                                    }}
                                    label="Email Status"
                                    aria-label="Select email status"
                                >
                                    {EMAIL_STATUSES.map((status) => (
                                        <MenuItem key={status.value} value={status.value}>
                                            {status.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Tooltip>
                    </Grid>

                    {/* Date Filter */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Tooltip title="Filter by specific date" placement="top">
                            <DatePicker
                                label="Event Date"
                                value={selectedDate}
                                onChange={(newValue) => {
                                    setSelectedDate(newValue);
                                    setRefreshGridData(true);
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth sx={{minWidth: 180}} aria-label="Select date"/>
                                )}
                                format="yyyy-MM-dd"
                            />
                        </Tooltip>
                    </Grid>

                    {/* Week Filter Dropdown */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Tooltip title="Select week number for display" placement="top">
                            <FormControl fullWidth sx={{minWidth: 140}}>
                                <InputLabel id="week-label">Week</InputLabel>
                                <Select
                                    labelId="week-label"
                                    value={selectedWeekDate || "all"}
                                    onChange={(e) => {
                                        setSelectedWeekDate(e.target.value ? parseInt(e.target.value, 10) : "all");
                                        setRefreshGridData(true);
                                    }}
                                    label="Week"
                                    aria-label="Select week number"
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    {Array.from({length: 52}, (_, i) => (
                                        <MenuItem key={i + 1} value={i + 1}>
                                            {i + 1}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Tooltip>
                    </Grid>

                    {/* Month Filter */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Tooltip title="Filter by month" placement="top">
                            <FormControl fullWidth sx={{minWidth: 180}}>
                                <InputLabel id="month-label">Month</InputLabel>
                                <Select
                                    labelId="month-label"
                                    value={selectedMonth}
                                    onChange={(e) => {
                                        setSelectedMonth(e.target.value);
                                        setRefreshGridData(true);
                                    }}
                                    label="Month"
                                    aria-label="Select month"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {Array.from({length: 12}, (_, i) => (
                                        <MenuItem key={i + 1} value={i + 1}>
                                            {format(new Date(2025, i, 1), "MMMM")}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Tooltip>
                    </Grid>

                    {/* Year Filter */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Tooltip title="Filter by year" placement="top">
                            <FormControl fullWidth sx={{minWidth: 180}}>
                                <InputLabel id="year-label">Year</InputLabel>
                                <Select
                                    labelId="year-label"
                                    value={selectedYear}
                                    onChange={(e) => {
                                        setSelectedYear(e.target.value);
                                        setRefreshGridData(true);
                                    }}
                                    label="Year"
                                    aria-label="Select year"
                                >
                                    {Array.from({length: 5}, (_, i) => {
                                        const year = new Date().getFullYear() + i; // 2 past, current, 2 future
                                        return (
                                            <MenuItem key={year} value={year.toString()}>
                                                {year}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Tooltip>
                    </Grid>

                    {/* Reset Filters Button */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Tooltip title="Clear all filters" placement="top">
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={resetFilters}
                                startIcon={<RestartAlt/>}
                                sx={{height: 56, minWidth: 180}}
                                aria-label="Reset all filters"
                                disabled={resetLoading}
                            >
                                Reset Filters
                            </Button>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Card>
        </LocalizationProvider>
    )
}

export default FilterControls;